import useMediaQuery from '@jouzen/ecom-components/hooks/useMediaQuery';
import { InverseHeader } from '@jouzen/ecom-components/layout';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useReducedMotion } from 'framer-motion';
import { type ReactNode, useEffect, useState } from 'react';

import { EventType } from '@/analytics/types';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

function ConditionalInverseHeader({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.large}px)`);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Before hydration, render children as-is
  if (!mounted) {
    return children;
  }

  // After hydration, conditionally render based on media query
  return isDesktop ? (
    <>{children}</>
  ) : (
    <InverseHeader enter exit>
      {children}
    </InverseHeader>
  );
}

export default function PerimenopauseHero() {
  const shouldReduceMotion = useReducedMotion();

  const handleShopTheRingClick = () => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'Shop The Ring',
      action: 'user moves to store',
      location: 'hero',
      path: '/store',
    });
  };

  return (
    <ConditionalInverseHeader>
      <section
        className="h-screen lg:pt-24 lg:pb-10"
        data-cy="perimenopause-hero"
      >
        <LayoutGrid className="h-full">
          <GridItem
            colStart={{ sm: 'full' }}
            colEnd={{ sm: 'full', lg: 13 }}
            className="h-full overflow-hidden lg:rounded-r-3xl"
          >
            <Motion
              className="h-full w-full"
              initial={{
                opacity: 0,
                filter: 'blur(5px)',
                scale: shouldReduceMotion ? 1 : 1.02,
              }}
              whileInView={{ opacity: 1, filter: 'blur(0px)', scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, type: 'tween' }}
            >
              <Image
                localizedAlt="perimenopause_lifestyle_alt_one"
                width={678}
                height={700}
                priority
                className="h-full w-full object-cover"
                src="perimenopause-lifestyle-one.jpg?fit=crop&crop=focalpoint&fp-x=0.52&fp-y=0.45&fp-z=1.55"
              />
            </Motion>
          </GridItem>
          <GridItem
            colStart={{ sm: 'main', lg: 14 }}
            colEnd={{ sm: 'main', lg: 'main' }}
          >
            <div className="flex h-full flex-col py-8 text-center lg:justify-center lg:pt-0">
              <Motion
                initial={{
                  opacity: 0,
                  y: shouldReduceMotion ? 0 : 15,
                  filter: 'blur(5px)',
                }}
                whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                viewport={{ once: true }}
                transition={{
                  duration: 1.25,
                  delay: 0.5,
                  type: 'tween',
                }}
              >
                <Typography
                  Element='h1'
                  variant="h3"
                  weight="normal"
                  className="mb-6 leading-tighter tracking-tighter text-balance"
                >
                  <RichText messageKey="perimenopause_hero_title" />
                </Typography>
                <Typography
                  variant="body-large"
                  weight="medium"
                  className="mb-6"
                >
                  <RichText messageKey="perimenopause_hero_copy" />
                </Typography>
                <div className="flex w-full flex-row justify-start">
                  <Button
                    className="w-fit"
                    href="/store"
                    onClick={handleShopTheRingClick}
                  >
                    <RichText messageKey="shop_the_ring" />
                  </Button>
                </div>
              </Motion>
            </div>
          </GridItem>
        </LayoutGrid>
      </section>
    </ConditionalInverseHeader>
  );
}
